import { TableBody as Body } from '@mui/material';
import dayjs from 'dayjs';
import { parseWallClockTime } from '../../../shared/Helpers/timezones';

import { HeaderProperties } from '../../../shared/uiComponents/Table/tableProperties';
import { CustomTableRow, RowTableCell } from '../../../shared/uiComponents/Table/tableHelpers';
import {
	ScheduleAllotmentReportRowProperties,
	ScheduleCancellationReportRowProperties,
	ScheduleReportRowProperties,
	ScheduleReportStatus,
	ScheduleUtilizationReportRowProperties,
} from '../../../redux/API/ClientAPIHelpers/scheduleReportProperties';

export const SoapStatusLabels: Record<number, string> = {
	[-1]: 'Not Created',
	[0]: 'Draft',
	[1]: 'Submitted',
	[2]: 'Expired (48h)',
	[3]: 'Expired (1 week)',
	[4]: 'Unlocked',
	[5]: 'Pending BT',
	[6]: 'Time/Location Mismatch',
};

const CancellationTypeLabels: Record<number, string> = {
	[0]: 'Single Session',
	[1]: 'Extended Time Off',
};

// Applied = no approval was ever required (e.g. single-session cancellations, or Admin-submitted
// extended time off) rather than a status on the approval path, hence "Approval Not Needed".
export const CancellationStatusLabels: Record<number, string> = {
	[0]: 'Approval Not Needed',
	[1]: 'Pending Approval',
	[2]: 'Approved',
	[3]: 'Denied',
};

const CancellingPartyLabels: Record<number, string> = {
	[0]: 'Provider',
	[1]: 'Client/Family',
};

const RescheduleIntentLabels: Record<number, string> = {
	[0]: 'Yes',
	[1]: 'No',
	[2]: 'Not Sure',
};

export const ScheduleReportStatusLabels: Record<ScheduleReportStatus, string> = {
	[ScheduleReportStatus.Completed]: 'Completed',
	[ScheduleReportStatus.OutstandingNoSession]: 'Scheduled Session Missed',
	[ScheduleReportStatus.OutstandingSoapMissing]: 'SOAP Not Submitted',
	[ScheduleReportStatus.OutstandingDraftExpired]: 'Expired Draft SOAP',
	[ScheduleReportStatus.CancelledSingleSession]: 'Cancelled - Single Session',
	[ScheduleReportStatus.CancelledExtendedTimeOff]: 'Cancelled - Extended Time Off',
	[ScheduleReportStatus.Scheduled]: 'Scheduled',
};

export const OutstandingSessionsHeaders: HeaderProperties[] = [
	{ id: '1', name: 'Service Date', orderBy: 'serviceDate' },
	{ id: '2', name: 'Session Time' },
	{ id: '3', name: 'Client', orderBy: 'clientName' },
	{ id: '4', name: 'Provider', orderBy: 'providerName' },
	{ id: '5', name: 'BT Email' },
	{ id: '6', name: 'Session Type' },
	{ id: '7', name: 'Status', orderBy: 'status' },
	{ id: '8', name: 'SOAP Status' },
	{ id: '9', name: 'Scheduled Hours', orderBy: 'scheduledHours' },
	{ id: '10', name: 'Completed Hours', orderBy: 'completedHours' },
	{ id: '11', name: 'Case Manager', orderBy: 'caseManagerName' },
	{ id: '12', name: 'ELC', orderBy: 'isElc' },
];

export const CancellationHeaders: HeaderProperties[] = [
	{ id: '1', name: 'Start Date', orderBy: 'startDate' },
	{ id: '2', name: 'End Date' },
	{ id: '3', name: 'Type' },
	{ id: '4', name: 'Status', orderBy: 'status' },
	{ id: '5', name: 'Client', orderBy: 'clientName' },
	{ id: '6', name: 'Provider', orderBy: 'providerName' },
	{ id: '7', name: 'Case Manager', orderBy: 'caseManagerName' },
	{ id: '8', name: 'Who' },
	{ id: '10', name: 'Reason', orderBy: 'reasonName' },
	{ id: '11', name: 'Reschedule?' },
	{ id: '12', name: 'Submitted By' },
	{ id: '13', name: 'Submitted At' },
	{ id: '14', name: 'ELC', orderBy: 'isElc' },
];

export const UtilizationHeaders: HeaderProperties[] = [
	{ id: '1', name: 'Client', orderBy: 'clientName' },
	{ id: '2', name: 'Provider', orderBy: 'providerName' },
	{ id: '3', name: 'Session Type' },
	{ id: '4', name: 'Case Manager', orderBy: 'caseManagerName' },
	{ id: '5', name: 'Scheduled Hours', orderBy: 'scheduledHours' },
	{ id: '6', name: 'Completed Hours', orderBy: 'completedHours' },
	{ id: '7', name: 'Cancelled Hours' },
	{ id: '8', name: 'Not Completed Hours' },
	{ id: '9', name: 'Schedule Adherence %', orderBy: 'scheduleAdherencePercentage' },
	{ id: '10', name: 'ELC', orderBy: 'isElc' },
];

export const AllotmentHeaders: HeaderProperties[] = [
	{ id: '1', name: 'Client', orderBy: 'clientName' },
	{ id: '2', name: 'Client Status' },
	{ id: '3', name: 'Case Manager', orderBy: 'caseManagerName' },
	{ id: '4', name: 'Has Schedule' },
	{ id: '6', name: 'Total Scheduled Hours', orderBy: 'totalScheduledHours' },
	{ id: '7', name: 'Total Authorized Hours', orderBy: 'totalAuthorizedHours' },
	{ id: '8', name: 'Off-Schedule Percentage', orderBy: 'authUtilizationPercentage' },
	{ id: '9', name: 'ELC', orderBy: 'isElc' },
];

const formatNumber = (value: number | null | undefined) => (typeof value === 'number' ? value.toFixed(2) : '-');
const formatDate = (value: string | null | undefined) => (value ? dayjs(value).format('MM-DD-YYYY') : '-');

const formatMinutes = (minutes: number | null | undefined) => {
	if (minutes === null || minutes === undefined) return null;
	return parseWallClockTime('2000-01-01T00:00:00').add(minutes, 'minute').format('h:mm A');
};

const formatSessionTime = (row: ScheduleReportRowProperties) => {
	if (row.sessionStartTime && row.sessionEndTime) {
		return `${parseWallClockTime(row.sessionStartTime).format('h:mm A')} - ${parseWallClockTime(row.sessionEndTime).format('h:mm A')}`;
	}
	const start = formatMinutes(row.scheduledStartTimeMinutes);
	const end = formatMinutes(row.scheduledEndTimeMinutes);
	return start && end ? `${start} - ${end}` : '-';
};

export const getScheduleReportRowKey = (row: ScheduleReportRowProperties) =>
	`${row.serviceDate}-${row.recurringScheduleId ?? row.sessionId ?? row.cancellationId}-${row.scheduledStartTimeMinutes ?? row.sessionStartTime ?? 'start'}-${row.scheduledEndTimeMinutes ?? row.sessionEndTime ?? 'end'}`;

export const OutstandingSessionsTableBody = ({ data }: { data: ScheduleReportRowProperties[] }) => (
	<Body>
		{data.map((row) => (
			<CustomTableRow key={getScheduleReportRowKey(row)}>
				<RowTableCell>{formatDate(row.serviceDate)}</RowTableCell>
				<RowTableCell>{formatSessionTime(row)}</RowTableCell>
				<RowTableCell>{row.clientName ?? '-'}</RowTableCell>
				<RowTableCell>{row.providerName ?? '-'}</RowTableCell>
				<RowTableCell>{row.providerEmail ?? '-'}</RowTableCell>
				<RowTableCell>{row.sessionTypeName ?? '-'}</RowTableCell>
				<RowTableCell>{ScheduleReportStatusLabels[row.status]}</RowTableCell>
				<RowTableCell>{row.soapStatus !== null && row.soapStatus !== undefined ? (SoapStatusLabels[row.soapStatus] ?? '-') : '-'}</RowTableCell>
				<RowTableCell>{formatNumber(row.scheduledHours)}</RowTableCell>
				<RowTableCell>{formatNumber(row.completedHours)}</RowTableCell>
				<RowTableCell>{row.caseManagerName ?? '-'}</RowTableCell>
				<RowTableCell>{row.isElc ? 'Yes' : ''}</RowTableCell>
			</CustomTableRow>
		))}
	</Body>
);

export const CancellationTableBody = ({ data }: { data: ScheduleCancellationReportRowProperties[] }) => (
	<Body>
		{data.map((row) => (
			<CustomTableRow key={row.cancellationId}>
				<RowTableCell>{formatDate(row.startDate)}</RowTableCell>
				<RowTableCell>{formatDate(row.endDate)}</RowTableCell>
				<RowTableCell>{CancellationTypeLabels[row.type] ?? row.type}</RowTableCell>
				<RowTableCell>{CancellationStatusLabels[row.status] ?? row.status}</RowTableCell>
				<RowTableCell>{row.clientName ?? '-'}</RowTableCell>
				<RowTableCell>{row.providerName ?? '-'}</RowTableCell>
				<RowTableCell>{row.caseManagerName ?? '-'}</RowTableCell>
				<RowTableCell>{row.cancellingParty !== null ? (CancellingPartyLabels[row.cancellingParty] ?? '-') : '-'}</RowTableCell>
				<RowTableCell>{row.reasonName ?? '-'}</RowTableCell>
				<RowTableCell>{row.rescheduleIntent !== null ? (RescheduleIntentLabels[row.rescheduleIntent] ?? '-') : '-'}</RowTableCell>
				<RowTableCell>{row.createdByName ?? '-'}</RowTableCell>
				<RowTableCell>{formatDate(row.createdAt)}</RowTableCell>
				<RowTableCell>{row.isElc ? 'Yes' : ''}</RowTableCell>
			</CustomTableRow>
		))}
	</Body>
);

export const UtilizationTableBody = ({ data }: { data: ScheduleUtilizationReportRowProperties[] }) => (
	<Body>
		{data.map((row) => (
			<CustomTableRow key={`${row.clientId}-${row.providerId}-${row.sessionTypeId}`}>
				<RowTableCell>{row.clientName ?? '-'}</RowTableCell>
				<RowTableCell>{row.providerName ?? '-'}</RowTableCell>
				<RowTableCell>{row.sessionTypeName ?? '-'}</RowTableCell>
				<RowTableCell>{row.caseManagerName ?? '-'}</RowTableCell>
				<RowTableCell>{formatNumber(row.scheduledHours)}</RowTableCell>
				<RowTableCell>{formatNumber(row.completedHours)}</RowTableCell>
				<RowTableCell>{formatNumber(row.cancelledHours)}</RowTableCell>
				<RowTableCell>{formatNumber(row.notCompletedHours)}</RowTableCell>
				<RowTableCell>{formatNumber(row.scheduleAdherencePercentage)}</RowTableCell>
				<RowTableCell>{row.isElc ? 'Yes' : ''}</RowTableCell>
			</CustomTableRow>
		))}
	</Body>
);

export const AllotmentTableBody = ({ data }: { data: ScheduleAllotmentReportRowProperties[] }) => (
	<Body>
		{data.map((row) => (
			<CustomTableRow key={row.clientId}>
				<RowTableCell>{row.clientName ?? '-'}</RowTableCell>
				<RowTableCell>{row.clientStatusName ?? '-'}</RowTableCell>
				<RowTableCell>{row.caseManagerName ?? '-'}</RowTableCell>
				<RowTableCell>{row.hasSchedule ? 'Yes' : 'No'}</RowTableCell>
				<RowTableCell>{formatNumber(row.totalScheduledHours)}</RowTableCell>
				<RowTableCell>{formatNumber(row.totalAuthorizedHours)}</RowTableCell>
				<RowTableCell>{formatNumber(row.authUtilizationPercentage)}</RowTableCell>
				<RowTableCell>{row.isElc ? 'Yes' : ''}</RowTableCell>
			</CustomTableRow>
		))}
	</Body>
);
